import { useTranslation } from 'react-i18next';
import { Column } from 'react-table';

import { Table } from '@/lib/components';
import { Flex } from '@/lib/v2/components/Layout/Stack';
import { EmptyScreenTable } from '@/lib/v2/examples';
import { EmptyActivityIcon, EmptyContactsIcon } from '@/lib/v2/icons/general';

import { formatLocalizedDate } from '@/src/application/util/formatLocalizedDate';
import { ITableECommerceActivity } from '@/src/ContactsModule/interfaces/Interfaces';
import { quantifier } from '@/src/ContactsModule/utils/quantifier';

export function ContactsProfileECommerceTable({
  activityECommerceList,
  activityCount,
  changeTableOrder,
  changeTablePage,
  totalPages,
  isLoading,
  gotoPageIndex,
  callToAction,
  setTableOrderByList,
  tableOrderByList,
}: ITableECommerceActivity) {
  const { t } = useTranslation();

  const truncateText = (value: string, maxLength: number) => {
    return value && value.length > maxLength ? value.slice(0, maxLength - 3) + '...' : value;
  };

  const dateColumn = {
    Header: () => (
      <Flex withGap alignment="start">
        <span className="font-medium text-[#004DBC]">{t('PROFILE_ECOMMERCE_TABLE.date')}</span>
      </Flex>
    ),
    accessor: 'date',
    id: 'date',
    Cell: ({ cell: { value } }: { cell: { value: Date } }) => (
      <Flex withGap alignment="start">
        <span className="self-start text-[#364365]">{value && formatLocalizedDate(value)}</span>
      </Flex>
    ),
  };
  const purchaseColumn = {
    Header: () => (
      <Flex withGap alignment="start">
        <span className="font-medium text-[#004DBC]">{t('PROFILE_ECOMMERCE_TABLE.purchase')}</span>
      </Flex>
    ),
    accessor: 'description',
    id: 'description',
    Cell: ({ cell: { value } }: { cell: { value: string } }) => (
      <Flex withGap alignment="start">
        <span className="self-start text-[#364365]" title={value}>
          {truncateText(value, 45)}
        </span>
      </Flex>
    ),
  };
  const amountColumn = {
    Header: () => (
      <Flex withGap alignment="start">
        <span className="font-medium text-[#004DBC]">{t('PROFILE_ECOMMERCE_TABLE.amount')}</span>
      </Flex>
    ),
    accessor: 'amount',
    id: 'amount',
    Cell: ({ cell: { value } }: { cell: { value: number } }) => (
      <Flex withGap alignment="start">
        <span className="self-start text-[#364365]">{value && quantifier(value, true)}</span>
      </Flex>
    ),
  };
  const statusInvoiceColumn = {
    Header: () => (
      <Flex withGap alignment="start">
        <span className="font-medium text-[#004DBC]">{t('PROFILE_ECOMMERCE_TABLE.status')}</span>
      </Flex>
    ),
    accessor: 'statusInvoice',
    id: 'statusInvoice',
    Cell: ({ cell: { value } }: { cell: { value: string } }) => (
      <Flex withGap alignment="start">
        <span className="self-start text-[#364365]" title={t(`PROFILE_ECOMMERCE_TABLE.${value}`)}>
          {value}
        </span>
      </Flex>
    ),
  };

  const activityTableColumns: Column<object>[] = [
    dateColumn,
    purchaseColumn,
    amountColumn,
    statusInvoiceColumn,
  ].filter((x) => x) as Column<object>[];

  return (
    <div className="h-full w-full text-base text-[#364365] 2xl:text-14">
      <Table
        // eslint-disable-next-line react/no-children-prop
        children={<></>}
        isPaginateOnOverflow
        withScrollbar
        classNamePagination="pr-4"
        columns={activityTableColumns}
        data={activityECommerceList}
        emptyScreen={{
          totalCount: activityCount || 0,
          noResult: (
            <EmptyScreenTable
              actionText={t('EMPTY_SCREEN_TABLE.activity')}
              description={t('EMPTY_SCREEN_TABLE.callToAction')}
              image={<EmptyContactsIcon />}
              title={t('EMPTY_SCREEN_TABLE.noResults')}
              onClickAction={callToAction && callToAction[0]}
            />
          ),
          noData: (
            <EmptyScreenTable
              actionText={t('EMPTY_SCREEN_TABLE.emptyActivityECommerceAction')}
              description={t('EMPTY_SCREEN_TABLE.emptyActivityECommerceDescription')}
              image={<EmptyActivityIcon />}
              title={t('EMPTY_SCREEN_TABLE.emptyActivityECommerceTitle')}
              onClickAction={callToAction && callToAction[0]}
            />
          ),
          noPerms: (
            <EmptyScreenTable
              actionText={t('EMPTY_SCREEN_TABLE.disabledActivityECommerceAction')}
              description={t('EMPTY_SCREEN_TABLE.disabledActivityECommerceDescription')}
              image={<EmptyActivityIcon />}
              title={t('EMPTY_SCREEN_TABLE.disabledActivityECommerceTitle')}
              onClickAction={callToAction && callToAction[0]}
            />
          ),
          perms:
            activityECommerceList &&
            activityECommerceList[0]?.error &&
            activityECommerceList[0].error,
        }}
        gotoPageIndex={gotoPageIndex}
        isLoading={isLoading}
        withCheckbox={false}
      />
    </div>
  );
}
